import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { toast } from 'sonner';
import { FileText, Database, Upload, Loader2, Check, ChevronDown, ChevronUp, Copy, Server, Globe, AlertCircle, RefreshCw, X, Trash2, Eraser } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function GoDaddyGuide({ egressIp }) {
  const [open, setOpen] = useState(false);
  const copyIp = () => {
    navigator.clipboard.writeText(egressIp || '');
    toast.success('IP adresa kopirana');
  };
  return (
    <div className="border border-zinc-800 rounded-lg">
      <button type="button" onClick={() => setOpen(o => !o)}
              data-testid="godaddy-guide-toggle"
              className="w-full flex items-center justify-between px-4 py-3 text-left">
        <span className="text-sm font-semibold text-sky-400 inline-flex items-center gap-2">
          <AlertCircle size={16} /> Upute za spajanje na GoDaddy MySQL bazu
        </span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-4">
          <div className="rounded-lg border-2 border-emerald-600/40 bg-emerald-500/5 p-4" data-testid="egress-ip-box">
            <div className="text-xs text-emerald-300 font-bold mb-1.5 uppercase tracking-wider flex items-center gap-2"><Server size={14}/> IP adresa ove platforme (za Remote MySQL)</div>
            <p className="text-[11px] text-zinc-400 mb-2">Ovu IP adresu morate dodati u cPanel → Remote MySQL kako bi se platforma mogla spojiti na vašu bazu:</p>
            <div className="flex items-center gap-2">
              <code className="bg-emerald-900/30 text-emerald-200 px-3 py-1.5 rounded font-mono text-sm">{egressIp || '…'}</code>
              <button onClick={copyIp} className="p-1.5 rounded border border-zinc-700 hover:border-emerald-500 text-zinc-400 hover:text-emerald-400" data-testid="egress-ip-copy">
                <Copy size={14} />
              </button>
            </div>
          </div>
          <div className="rounded-lg border-2 border-amber-600/40 bg-amber-500/5 p-4 text-xs leading-relaxed text-zinc-300 space-y-3">
            <div className="font-bold text-amber-300 flex items-center gap-2"><Globe size={14}/> cPanel Remote MySQL — upute za ispunjavanje forme</div>
            <div>Idite na <strong>cPanel → sekcija "Databases" → kliknite "Remote MySQL®"</strong></div>
            <div>
              <div className="font-bold uppercase text-[10px] tracking-wider text-amber-200">Polje: Host (IP address to allow)</div>
              <code className="block bg-amber-900/20 px-2 py-1 rounded mt-1 text-amber-100">{egressIp || '…'}</code>
            </div>
            <div>
              <div className="font-bold uppercase text-[10px] tracking-wider text-amber-200">Polje: Comment (opis)</div>
              <code className="block bg-amber-900/20 px-2 py-1 rounded mt-1 text-amber-100">E-mina.eu Import Platform</code>
            </div>
            <div>
              <div className="font-bold uppercase text-[10px] tracking-wider text-amber-200">Zatim kliknite</div>
              <p>Kliknite gumb <strong>"Add Host"</strong> za spremanje. Pričekajte 1-2 minute prije nego pokušate uvoz.</p>
            </div>
          </div>
          <div className="rounded-lg bg-sky-500/5 border border-sky-600/40 p-4 text-xs space-y-2 text-zinc-300 leading-relaxed">
            <div className="font-semibold text-sky-300">Kako pronaći podatke za spajanje na GoDaddy:</div>
            <ol className="space-y-1 list-decimal list-inside">
              <li>Prijavite se u GoDaddy cPanel (obično: <code className="text-sky-200">vasadomena.com/cpanel</code> ili putem GoDaddy dashboard → My Products → Hosting → Manage).</li>
              <li>Pronađite <strong>"MySQL Databases"</strong> u sekciji Databases.</li>
              <li><strong>Host:</strong> NE koristite "localhost" — pronađite hostname na vrhu stranice ili pod "Database Details". Obično: <code className="text-sky-200">p3plcpnlXXXX.prod.phx3.secureserver.net</code></li>
              <li><strong>Baza:</strong> Ime baze ima format <code className="text-sky-200">cpaneluser_dbname</code></li>
              <li><strong>Korisnik:</strong> Korisničko ime obično ima format <code className="text-sky-200">cpaneluser_username</code></li>
              <li><strong className="text-red-400">OBAVEZNO — Remote MySQL:</strong> U cPanelu pronađite "Remote MySQL" i dodajte IP adresu <code className="text-red-300">{egressIp || '…'}</code>. Bez toga, spajanje izvana neće raditi!</li>
              <li><strong>Prefiks tablica:</strong> Provjerite u <code>wp-config.php</code> (<code>$table_prefix</code>). Standardno: <code>wp_</code></li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}

function XmlTab({ authFetch, onStarted }) {
  const [file, setFile] = useState(null);
  const [reimport, setReimport] = useState(false);
  const [aiCats, setAiCats] = useState(false);
  const [downloadImages, setDownloadImages] = useState(true);
  const [uploading, setUploading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!file) { toast.error('Odaberite .xml datoteku'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('reimport', reimport);
      fd.append('ai_categories', aiCats);
      fd.append('download_images', downloadImages);
      const r = await authFetch(`${API}/admin/import/wp/xml`, { method: 'POST', body: fd, headers: {} });
      const txt = await r.text();
      let d;
      try { d = txt ? JSON.parse(txt) : {}; } catch { d = { detail: txt || `HTTP ${r.status}` }; }
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      toast.success(`Uvoz započet (${d.job_id})`);
      onStarted(d.job_id);
    } catch (err) { toast.error(err.message || 'Greška pri uvozu'); } finally { setUploading(false); }
  }

  return (
    <form onSubmit={submit} className="space-y-4 max-w-2xl" data-testid="wp-xml-form">
      <div>
        <label className="block text-xs font-semibold text-zinc-400 mb-2">WordPress eksport datoteka (.xml / .wxr)</label>
        <label className="flex items-center gap-3 px-4 py-8 border-2 border-dashed border-zinc-700 rounded-xl hover:border-red-500 cursor-pointer transition-colors">
          <Upload size={20} className="text-zinc-500" />
          <div className="text-sm">
            <div className="font-semibold">{file ? file.name : 'Kliknite ili ispustite WXR datoteku ovdje'}</div>
            <div className="text-xs text-zinc-500">Max 200 MB</div>
          </div>
          <input type="file" accept=".xml,.wxr" onChange={e => setFile(e.target.files[0])} className="sr-only" data-testid="wp-xml-file" />
        </label>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={reimport} onChange={e => setReimport(e.target.checked)} data-testid="wp-xml-reimport" />
        <span>Reimport (izbriši prethodno uvezene članke)</span>
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={aiCats} onChange={e => setAiCats(e.target.checked)} data-testid="wp-xml-ai-cats" />
        <span>AI detekcija kategorije (koristi AI za automatsko dodjeljivanje kategorija)</span>
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={downloadImages} onChange={e => setDownloadImages(e.target.checked)} data-testid="wp-xml-download-images" />
        <span>Preuzmi sve slike iz članaka i galerija lokalno (preporučeno)</span>
      </label>
      <button type="submit" disabled={uploading || !file}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 rounded-md text-white font-medium disabled:opacity-50"
              data-testid="wp-xml-submit">
        {uploading ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Uvezi XML
      </button>
    </form>
  );
}

function MySqlTab({ authFetch, egressIp, onStarted }) {
  const [cfg, setCfg] = useState({
    host: '', port: 3306, database: '', username: '', password: '',
    table_prefix: 'wp_', max_articles: 10000, reimport: false, ai_categories: false,
    wp_base_url: '', download_images: true, use_rest_api_fallback: true,
    start_index: 0, resume_after: '', skip_existing: true,
  });
  const [testing, setTesting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  async function testConn() {
    setTesting(true); setTestResult(null);
    try {
      const r = await authFetch(`${API}/admin/import/wp/mysql/test`, { method: 'POST', body: JSON.stringify(cfg) });
      const txt = await r.text();
      let d;
      try { d = txt ? JSON.parse(txt) : {}; } catch { d = { detail: txt || `HTTP ${r.status}` }; }
      if (!r.ok) throw new Error(d.detail || 'Test failed');
      setTestResult({ ok: true, count: d.posts_count });
      toast.success(`Spajanje uspješno. Nađeno ${d.posts_count} članaka.`);
    } catch (err) {
      setTestResult({ ok: false, error: err.message });
      toast.error(`Test neuspješan: ${err.message}`);
    } finally { setTesting(false); }
  }

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await authFetch(`${API}/admin/import/wp/mysql`, { method: 'POST', body: JSON.stringify(cfg) });
      const txt = await r.text();
      let d;
      try { d = txt ? JSON.parse(txt) : {}; } catch { d = { detail: txt || `HTTP ${r.status}` }; }
      if (!r.ok) throw new Error(d.detail || 'Greška');
      toast.success(`Uvoz započet (${d.job_id})`);
      onStarted(d.job_id);
    } catch (err) { toast.error(err.message || 'Greška pri uvozu'); } finally { setSubmitting(false); }
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="wp-mysql-form">
      <div>
        <h3 className="font-serif text-xl text-zinc-100 mb-1">Uvoz iz WordPress MySQL baze</h3>
        <p className="text-xs text-zinc-400 mb-4">Spojite se direktno na vašu WordPress MySQL bazu za uvoz članaka.</p>
        <GoDaddyGuide egressIp={egressIp} />
      </div>
      <div className="grid md:grid-cols-[1fr,140px] gap-4">
        <div>
          <label className="block text-xs font-semibold text-zinc-400 mb-1.5">MySQL Host <span className="text-red-400">*</span></label>
          <input required value={cfg.host} onChange={e => setCfg({ ...cfg, host: e.target.value })}
                 placeholder="npr. p3plcpnl0000.prod.phx3.secureserver.net"
                 data-testid="wp-mysql-host"
                 className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-zinc-400 mb-1.5">Port</label>
          <input type="number" value={cfg.port} onChange={e => setCfg({ ...cfg, port: +e.target.value || 3306 })}
                 data-testid="wp-mysql-port"
                 className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-zinc-400 mb-1.5">Naziv baze <span className="text-red-400">*</span></label>
          <input required value={cfg.database} onChange={e => setCfg({ ...cfg, database: e.target.value })}
                 placeholder="npr. korisnik_wordpress" data-testid="wp-mysql-database"
                 className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-zinc-400 mb-1.5">Korisničko ime <span className="text-red-400">*</span></label>
          <input required value={cfg.username} onChange={e => setCfg({ ...cfg, username: e.target.value })}
                 placeholder="npr. korisnik_wpuser" data-testid="wp-mysql-username"
                 className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-zinc-400 mb-1.5">Lozinka <span className="text-red-400">*</span></label>
          <input required type="password" value={cfg.password} onChange={e => setCfg({ ...cfg, password: e.target.value })}
                 placeholder="Lozinka za MySQL" data-testid="wp-mysql-password"
                 className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-zinc-400 mb-1.5">Prefiks tablica</label>
          <input value={cfg.table_prefix} onChange={e => setCfg({ ...cfg, table_prefix: e.target.value })}
                 data-testid="wp-mysql-prefix"
                 className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm font-mono" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-zinc-400 mb-1.5">Maks. broj članaka</label>
        <input type="number" value={cfg.max_articles} onChange={e => setCfg({ ...cfg, max_articles: +e.target.value || 10000 })}
               data-testid="wp-mysql-max"
               className="w-full md:w-64 bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
      </div>
      <div>
        <label className="block text-xs font-semibold text-zinc-400 mb-1.5">Nastavi od indeksa (za prekinuti posao)</label>
        <input type="number" min="0" value={cfg.start_index} onChange={e => setCfg({ ...cfg, start_index: Math.max(0, +e.target.value || 0) })}
               data-testid="wp-mysql-start-index"
               placeholder="0"
               className="w-full md:w-64 bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
        <p className="mt-1 text-[11px] text-zinc-500">Ostavite <code>0</code> za novi uvoz. Ako je prethodni posao prekinut, upišite broj iz "Obrađeno" stupca ispod.</p>
      </div>
      <div>
        <label className="block text-xs font-semibold text-zinc-400 mb-1.5">Nastavi nakon članka (naslov ili slug)</label>
        <input type="text" value={cfg.resume_after} onChange={e => setCfg({ ...cfg, resume_after: e.target.value })}
               data-testid="wp-mysql-resume-after"
               placeholder="npr. 'najnovije-vijesti-iz-cakovca' ili dio naslova"
               className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
        <p className="mt-1 text-[11px] text-zinc-500">Sustav preskače sve članke do ovog marker-a (uključujući), a zatim nastavlja uvoz. Korisno kad znate naslov zadnjeg uspješno uvezenog članka. Ostavite prazno ako ne koristite.</p>
      </div>
      <label className="flex items-start gap-2 text-sm p-3 rounded-md bg-emerald-500/5 border border-emerald-600/30">
        <input type="checkbox" checked={cfg.skip_existing} onChange={e => setCfg({ ...cfg, skip_existing: e.target.checked })}
               data-testid="wp-mysql-skip-existing" className="mt-0.5" />
        <span>
          <strong className="text-emerald-200">Preskoči već uvezene članke (preporučeno)</strong>
          <span className="block text-[11px] text-zinc-400 mt-0.5">Ako slug ili izvorni URL već postoji u bazi, članak se ne uvozi ponovno. Pokrenite isti uvoz bilo kad da dopunite samo razliku (nove članke).</span>
        </span>
      </label>

      <div className="rounded-lg border border-sky-600/30 bg-sky-500/5 p-4 space-y-3">
        <div className="text-xs font-bold uppercase tracking-wider text-sky-300">Migracija medija &amp; sadržaja (preporučeno)</div>
        <div>
          <label className="block text-xs font-semibold text-zinc-400 mb-1.5">Izvorna WordPress adresa (npr. <code>https://emina.eu</code>)</label>
          <input value={cfg.wp_base_url} onChange={e => setCfg({ ...cfg, wp_base_url: e.target.value })}
                 placeholder="https://vas-domain.com"
                 data-testid="wp-mysql-base-url"
                 className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm" />
          <p className="mt-1 text-[11px] text-zinc-500">Ako ostavite prazno, platforma pokušava automatski pročitati <code>siteurl</code> iz <code>wp_options</code>. Potrebno za preuzimanje slika i REST fallback.</p>
        </div>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={cfg.download_images} onChange={e => setCfg({ ...cfg, download_images: e.target.checked })}
                 data-testid="wp-mysql-download-images" className="mt-0.5" />
          <span>
            <strong className="text-zinc-200">Preuzmi sve slike lokalno</strong>
            <span className="block text-[11px] text-zinc-500">Skida featured slike i sve <code>&lt;img&gt;</code> iz sadržaja članka (uključujući galerije) na server pod <code>/api/uploads/wp/…</code> i prepisuje URL-ove.</span>
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={cfg.use_rest_api_fallback} onChange={e => setCfg({ ...cfg, use_rest_api_fallback: e.target.checked })}
                 data-testid="wp-mysql-rest-fallback" className="mt-0.5" />
          <span>
            <strong className="text-zinc-200">WP REST API fallback</strong>
            <span className="block text-[11px] text-zinc-500">Kad je <code>post_content</code> prazan (Elementor / Divi / Gutenberg blokovi), dohvaća renderirani HTML s <code>/wp-json/wp/v2/posts</code> da biste imali pun tekst i galerije.</span>
          </span>
        </label>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={cfg.reimport} onChange={e => setCfg({ ...cfg, reimport: e.target.checked })}
               data-testid="wp-mysql-reimport" />
        <span>Reimport (izbriši prethodno uvezene članke)</span>
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={cfg.ai_categories} onChange={e => setCfg({ ...cfg, ai_categories: e.target.checked })}
               data-testid="wp-mysql-ai-cats" />
        <span>AI detekcija kategorije (koristi AI za automatsko dodjeljivanje kategorija)</span>
      </label>
      {testResult && (
        <div className={`rounded-md p-3 text-sm flex items-start gap-2 ${testResult.ok ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-600/30' : 'bg-red-500/10 text-red-300 border border-red-600/30'}`} data-testid="wp-mysql-test-result">
          {testResult.ok ? <Check size={16} className="shrink-0 mt-0.5" /> : <AlertCircle size={16} className="shrink-0 mt-0.5"/>}
          {testResult.ok ? <div>Spajanje uspješno. Nađeno <strong>{testResult.count}</strong> WordPress članaka.</div> : <div>{testResult.error}</div>}
        </div>
      )}
      <div className="flex items-center gap-3">
        <button type="button" onClick={testConn} disabled={testing || !cfg.host || !cfg.database || !cfg.username}
                className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-700 hover:border-sky-500 hover:text-sky-400 rounded-md text-sm disabled:opacity-40"
                data-testid="wp-mysql-test">
          {testing ? <Loader2 size={14} className="animate-spin" /> : <Server size={14} />} Testiraj spajanje
        </button>
        <button type="submit" disabled={submitting || !cfg.host}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-500 hover:bg-red-600 rounded-md text-white font-medium disabled:opacity-50"
                data-testid="wp-mysql-submit">
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <Database size={14} />} Spoji se i uvezi
        </button>
      </div>
    </form>
  );
}

function JobStatusList({ authFetch }) {
  const [jobs, setJobs] = useState([]);
  const [cancelling, setCancelling] = useState({});
  const [deleting, setDeleting] = useState({});
  const [clearing, setClearing] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/admin/import/wp/jobs?limit=10`);
      if (!r.ok) return;
      const d = await r.json();
      setJobs(d.jobs || []);
    } catch { /* noop */ }
  }, [authFetch]);
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);

  async function cancelJob(jobId) {
    if (cancelling[jobId]) return;
    if (!window.confirm('Prekinuti ovaj uvoz? Već obrađeni članci ostaju u bazi.')) return;
    setCancelling(c => ({ ...c, [jobId]: true }));
    try {
      // bulk-refetch jobs live under a different route — all other modes under /admin/import/wp/jobs/{id}/cancel.
      const mode = jobs.find(j => j.id === jobId)?.mode || '';
      const url = mode === 'bulk-refetch'
        ? `${API}/admin/articles/bulk-refetch-images/${jobId}/cancel`
        : `${API}/admin/import/wp/jobs/${jobId}/cancel`;
      const r = await authFetch(url, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Prekid nije uspio');
      toast.success('Prekid poslan — zadatak se zaustavlja.');
      load();
    } catch (e) {
      toast.error(e.message || 'Prekid nije uspio');
    } finally {
      setCancelling(c => ({ ...c, [jobId]: false }));
    }
  }

  async function deleteJob(jobId) {
    if (deleting[jobId]) return;
    if (!window.confirm('Obrisati ovaj zapis? (Uvezeni članci ostaju — briše se samo zapis o zadatku.)')) return;
    setDeleting(d => ({ ...d, [jobId]: true }));
    try {
      const r = await authFetch(`${API}/admin/import/wp/jobs/${jobId}`, { method: 'DELETE' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Brisanje nije uspjelo');
      toast.success('Zapis obrisan');
      setJobs(prev => prev.filter(j => j.id !== jobId));
    } catch (e) {
      toast.error(e.message || 'Brisanje nije uspjelo');
    } finally {
      setDeleting(d => ({ ...d, [jobId]: false }));
    }
  }

  async function clearAll() {
    if (clearing) return;
    if (!window.confirm('Obrisati sve završene, prekinute i neuspjele zadatke? Aktivni se ne diraju.')) return;
    setClearing(true);
    try {
      const r = await authFetch(`${API}/admin/import/wp/jobs`, { method: 'DELETE' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Brisanje nije uspjelo');
      toast.success(`Obrisano ${d.deleted || 0} zapis(a)`);
      load();
    } catch (e) {
      toast.error(e.message || 'Brisanje nije uspjelo');
    } finally {
      setClearing(false);
    }
  }

  return (
    <section className="mt-10 pt-6 border-t border-zinc-800" data-testid="wp-jobs-list">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-400">Zadaci uvoza</h3>
        {jobs.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            disabled={clearing}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-zinc-700 hover:border-amber-500 hover:text-amber-400 text-zinc-400 rounded text-[11px] font-semibold uppercase tracking-wider disabled:opacity-50"
            title="Obriši sve neaktivne zadatke"
            data-testid="wp-jobs-clear-all"
          >
            {clearing ? <Loader2 size={12} className="animate-spin" /> : <Eraser size={12} />}
            Očisti sve
          </button>
        )}
      </div>
      {jobs.length === 0 ? (
        <p className="text-xs text-zinc-500 italic py-4" data-testid="wp-jobs-empty">Nema zapisanih zadataka uvoza.</p>
      ) : (
      <div className="space-y-2">
        {jobs.map(j => {
          const pct = j.total ? Math.round((j.processed / j.total) * 100) : 0;
          const canCancel = j.status === 'running';
          const canDelete = j.status !== 'running';
          const isInterrupted = j.status === 'interrupted';
          return (
            <div key={j.id} className="rounded-md border border-zinc-800 bg-zinc-900/40 p-3 text-xs" data-testid={`wp-job-${j.id}`}>
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-zinc-400">{j.id}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider ${
                      j.status === 'completed' ? 'bg-emerald-600/20 text-emerald-300' :
                      j.status === 'failed' ? 'bg-red-600/20 text-red-300' :
                      j.status === 'cancelled' ? 'bg-zinc-600/40 text-zinc-300' :
                      j.status === 'interrupted' ? 'bg-amber-600/25 text-amber-300' :
                      'bg-sky-600/20 text-sky-300'
                    }`}>{j.status}</span>
                    <span className="text-zinc-500">{j.mode}</span>
                  </div>
                  <div className="text-zinc-400 mt-0.5">Obrađeno {j.processed}/{j.total || '?'} · uvezeno <strong className="text-emerald-300">{j.imported}</strong> · preskočeno <strong className="text-zinc-300">{j.skipped}</strong></div>
                  {j.last_processed_title && (
                    <div className="text-zinc-500 mt-0.5 text-[11px] italic truncate">Zadnji članak: {j.last_processed_title}</div>
                  )}
                  {j.status === 'running' && (
                    <div className="w-full h-1 bg-zinc-800 rounded mt-2 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-red-500 to-red-400" style={{ width: `${pct}%` }} />
                    </div>
                  )}
                  {j.error && <div className={`${isInterrupted ? 'text-amber-300' : 'text-red-400'} mt-1`}>{j.error}</div>}
                  {Array.isArray(j.log) && j.log.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-[11px] text-zinc-500 cursor-pointer hover:text-zinc-300">Prikaži zapis ({j.log.length})</summary>
                      <div className="mt-2 max-h-48 overflow-auto bg-black/40 border border-zinc-800 rounded p-2 text-[11px] font-mono space-y-0.5" data-testid={`wp-job-log-${j.id}`}>
                        {j.log.slice(-60).map((e, i) => (
                          <div key={i} className={`${e.level === 'error' ? 'text-red-400' : e.level === 'warn' ? 'text-amber-300' : 'text-zinc-400'}`}>
                            <span className="text-zinc-600">{(e.ts || '').slice(11, 19)}</span> · {e.message}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
                <div className="flex flex-col gap-1 items-end">
                  {canCancel && (
                    <button
                      type="button"
                      onClick={() => cancelJob(j.id)}
                      disabled={!!cancelling[j.id]}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-red-600/50 text-red-300 hover:bg-red-600/10 rounded text-[11px] font-semibold uppercase tracking-wider disabled:opacity-50 whitespace-nowrap"
                      title="Prekini ovaj zadatak"
                      data-testid={`wp-job-cancel-${j.id}`}
                    >
                      {cancelling[j.id] ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
                      Prekini
                    </button>
                  )}
                  {canDelete && (
                    <button
                      type="button"
                      onClick={() => deleteJob(j.id)}
                      disabled={!!deleting[j.id]}
                      className="inline-flex items-center gap-1.5 px-2 py-1 border border-zinc-700 text-zinc-500 hover:border-red-500 hover:text-red-400 rounded text-[10px] font-semibold uppercase tracking-wider disabled:opacity-50 whitespace-nowrap"
                      title="Obriši zapis"
                      data-testid={`wp-job-delete-${j.id}`}
                    >
                      {deleting[j.id] ? <Loader2 size={10} className="animate-spin" /> : <Trash2 size={10} />}
                      Obriši
                    </button>
                  )}
                  {isInterrupted && j.mode === 'mysql' && (
                    <span className="text-[10px] text-amber-300/80 whitespace-nowrap" title="Za nastavak ponovite MySQL formu i upišite start_index ili naslov članka">
                      Nastavi od {j.processed}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}
    </section>
  );
}

export default function WordPressImport() {
  const { authFetch } = useAdmin();
  const [tab, setTab] = useState('xml');
  const [egressIp, setEgressIp] = useState('');
  const [, setRefreshTick] = useState(0);
  const [rescanning, setRescanning] = useState(false);

  useEffect(() => {
    authFetch(`${API}/admin/import/wp/egress-ip`).then(r => r.json()).then(d => setEgressIp(d.ip || '')).catch(() => {});
  }, [authFetch]);

  async function handleBulkRescan() {
    if (rescanning) return;
    if (!window.confirm('Pokrenuti ponovno skeniranje galerija za sve WP članke kojima nedostaje galerija? Zadatak se izvršava u pozadini — pratite napredak ispod.')) return;
    setRescanning(true);
    try {
      const r = await authFetch(`${API}/admin/articles/bulk-refetch-images?limit=2000&only_missing_gallery=true`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Pokretanje nije uspjelo');
      toast.success(`Ponovno skeniranje pokrenuto — ${d.matched} članaka u redu (${d.job_id}).`);
      setRefreshTick(x => x + 1);
    } catch (e) {
      toast.error(e.message || 'Greška pri pokretanju.');
    } finally {
      setRescanning(false);
    }
  }

  async function handleImageOnlyReimport() {
    const baseUrl = window.prompt(
      'Migracija slika: unesite originalnu WordPress adresu (npr. https://emina.eu) za reimport slika svih uvezenih članaka. Ostavite prazno za korištenje pohranjenog source_url-a.',
      '',
    );
    if (baseUrl === null) return;
    if (!window.confirm('Pokrenuti reimport slika za do 2000 WP članaka uz polomljene cover-e? Postojeće lokalne slike se ne preuzimaju ponovno.')) return;
    setRescanning(true);
    try {
      const params = new URLSearchParams({
        limit: '2000', only_missing_gallery: 'false', only_remote_cover: 'true',
        ...(baseUrl ? { wp_base_url: baseUrl } : {}),
      });
      const r = await authFetch(`${API}/admin/articles/bulk-refetch-images?${params}`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Pokretanje nije uspjelo');
      toast.success(`Reimport slika pokrenut — ${d.matched} članaka (${d.job_id}).`);
      setRefreshTick(x => x + 1);
    } catch (e) { toast.error(e.message || 'Greška pri pokretanju.'); }
    finally { setRescanning(false); }
  }

  return (
    <div className="p-6 max-w-5xl" data-testid="wp-import-page">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-zinc-100">WordPress Import</h1>
          <p className="text-xs text-zinc-400 mt-1">Uvezite članke iz WordPress eksporta</p>
        </div>
        <div className="inline-flex items-center gap-2">
          <button
            type="button"
            onClick={handleImageOnlyReimport}
            disabled={rescanning}
            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-md border border-sky-600/40 text-sky-200 hover:bg-sky-600/10 disabled:opacity-50 whitespace-nowrap"
            title="Migrira / ponovno preuzima sve slike (cover + galerija) za uvezene WP članke s opcionalnim novim base URL-om za rekonektirani izvor"
            data-testid="image-only-reimport"
          >
            <RefreshCw size={14} />
            Migracija slika (cover + galerija)
          </button>
          <button
            type="button"
            onClick={handleBulkRescan}
            disabled={rescanning}
            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-md border border-amber-600/40 text-amber-200 hover:bg-amber-600/10 disabled:opacity-50 whitespace-nowrap"
            title="Prolazi kroz sve uvezene članke i ponovno dohvaća Elementor galerije koje su prvi uvoz propustio"
            data-testid="bulk-rescan-galleries"
          >
            {rescanning ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Ponovno skeniraj galerije
          </button>
        </div>
      </header>

      <div className="inline-flex p-1 bg-zinc-900 rounded-lg border border-zinc-800 mb-5" data-testid="wp-import-tabs">
        {[
          { key: 'xml', label: 'XML Export', icon: FileText },
          { key: 'mysql', label: 'MySQL baza', icon: Database },
        ].map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
                    data-testid={`wp-tab-${t.key}`}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${active ? 'bg-white text-zinc-900 shadow' : 'text-zinc-400 hover:text-zinc-200'}`}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-6">
        {tab === 'xml'
          ? <XmlTab authFetch={authFetch} onStarted={() => setRefreshTick(x => x + 1)} />
          : <MySqlTab authFetch={authFetch} egressIp={egressIp} onStarted={() => setRefreshTick(x => x + 1)} />}
      </div>

      <JobStatusList authFetch={authFetch} />
    </div>
  );
}
